import type {
  BankDetailRow,
  CompanyProfileRow,
  SmtpSettingRow,
} from "../types/settings.types";

function asRecord(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  return raw as Record<string, unknown>;
}

function pickStr(r: Record<string, unknown>, camel: string, snake?: string) {
  const v = r[camel] ?? (snake != null ? r[snake] : undefined);
  if (v == null) return "";
  return String(v);
}

export function normalizeCompanyRow(raw: unknown): CompanyProfileRow | null {
  const r = asRecord(raw);
  if (!r) return null;
  const name = pickStr(r, "name").trim();
  if (!name) return null;
  return {
    id: r.id != null ? Number(r.id) : undefined,
    name,
    address: pickStr(r, "address") || null,
    phone: pickStr(r, "phone") || null,
    email: pickStr(r, "email") || null,
    gstNumber: pickStr(r, "gstNumber", "gst_number") || null,
  };
}

export function normalizeBankRow(raw: unknown): BankDetailRow | null {
  const r = asRecord(raw);
  if (!r) return null;
  return {
    id: r.id != null ? Number(r.id) : undefined,
    companyId:
      r.companyId != null
        ? Number(r.companyId)
        : r.company_id != null
          ? Number(r.company_id)
          : undefined,
    accountName: pickStr(r, "accountName", "account_name"),
    accountNumber: pickStr(r, "accountNumber", "account_number"),
    ifscCode: pickStr(r, "ifscCode", "ifsc_code"),
    bankName: pickStr(r, "bankName", "bank_name"),
    branch: pickStr(r, "branch"),
  };
}

export function normalizeSmtpRow(raw: unknown): SmtpSettingRow | null {
  const r = asRecord(raw);
  if (!r) return null;
  const host = pickStr(r, "host").trim();
  const fromEmail = pickStr(r, "fromEmail", "from_email").trim();
  if (!host || !fromEmail) return null;
  const portRaw = r.port;
  const port =
    portRaw != null && portRaw !== ""
      ? Number(portRaw)
      : 587;
  const secure = r.secure === true || r.secure === "true" || r.secure === 1;
  return {
    id: r.id != null ? Number(r.id) : undefined,
    companyId:
      r.companyId != null
        ? Number(r.companyId)
        : r.company_id != null
          ? Number(r.company_id)
          : undefined,
    host,
    port: Number.isFinite(port) ? port : 587,
    secure,
    username: pickStr(r, "username") || null,
    fromEmail,
    fromName: pickStr(r, "fromName", "from_name") || null,
  };
}

export function companyToFormValues(
  c: CompanyProfileRow | null,
): import("../types/settings.types").CompanyFormValues {
  if (!c) {
    return {
      name: "",
      address: "",
      phone: "",
      email: "",
      gstNumber: "",
    };
  }
  return {
    name: c.name,
    address: c.address ?? "",
    phone: c.phone ?? "",
    email: c.email ?? "",
    gstNumber: c.gstNumber ?? "",
  };
}

export function bankToFormValues(
  b: BankDetailRow | null,
): import("../types/settings.types").BankFormValues {
  if (!b) {
    return {
      accountName: "",
      accountNumber: "",
      ifscCode: "",
      bankName: "",
      branch: "",
    };
  }
  return {
    accountName: b.accountName,
    accountNumber: b.accountNumber,
    ifscCode: b.ifscCode,
    bankName: b.bankName,
    branch: b.branch,
  };
}

export function smtpToFormValues(
  s: SmtpSettingRow | null,
): import("../types/settings.types").SmtpFormValues {
  if (!s) {
    return {
      host: "",
      port: "587",
      secure: true,
      username: "",
      fromEmail: "",
      fromName: "",
      password: "",
      clearStoredPassword: false,
    };
  }
  return {
    host: s.host,
    port: String(s.port ?? 587),
    secure: s.secure !== false,
    username: s.username ?? "",
    fromEmail: s.fromEmail,
    fromName: s.fromName ?? "",
    password: "",
    clearStoredPassword: false,
  };
}
